'use client';

import { useConstant } from '@cc-shopping/hooks/useConstant';
import { useIsMounted } from '@cc-shopping/hooks/useIsMounted';
import { useAnalyticsPageView } from '@cc-shopping/platform-analytics/AnalyticsPageView/Context';
import { useAnalyticsSkuViewedContext } from '@cc-shopping/platform-analytics/AnalyticsSkuViewed/Context';
import { useNonMonetizingPageAction } from '@cc-shopping/platform-analytics/hooks/useNonMonetizingPageAction';
import { CTASearchParams, type CtaSearchParamAction, generateSearchParameters, type SearchParamValue } from '@cc-shopping/platform-analytics/lib/ctaSearchParams';
import { createHandleOnClick, transformApplyLink } from '@cc-shopping/platform-analytics/lib/shoppingFinish';
import { getCleanLocationHref } from '@cc-shopping/platform-analytics/lib/urlUtils';
import type { CtaAnalytics, FinishType, LinkType } from '@cc-shopping/platform-analytics/types';
import { env } from '@cc-shopping/platform-env';
import { datadogRum } from '@datadog/browser-rum';
import { Link, type LinkProps } from '@mui/material';
import React, { type PropsWithChildren, useCallback, useMemo } from 'react';
import { getFinishTypeForApplyLink } from 'src/features/ChaseLandingPages/applyLink';
import { useChaseLandingPageLink } from 'src/features/ChaseLandingPages/useChaseLandingPageLink';
import { useCardPosition } from 'src/features/roundup-filters/CardPositionContext';
import { useExperimentalApplyLink } from 'src/shared/hooks/useExperimentalApplyLink';
import { v4 as uuid } from 'uuid';
import { addNoopener, ctaLinkRegexParsing } from './utils';
export type CtaAnalyticsProps = Omit<CtaAnalytics, 'type' | 'href'>;
export type ElementWithAnchorProps = React.ComponentType<LinkProps>;
interface CtaLinkInternalProps<TLink extends ElementWithAnchorProps> {
  type?: LinkType;
  analytics: CtaAnalyticsProps;
  href: string;
  finishType?: FinishType;
  Component?: TLink | typeof Link;
  isMonetizing?: boolean;
  isReview?: boolean;
  onClickAnalyticsCallback?: () => void;
  parameterActions: CtaSearchParamAction[];
  skipShoppingFinishedEvent?: boolean;
  shoppingFinishedId?: string;
}
export type CtaLinkProps<TLink extends ElementWithAnchorProps = typeof Link> = PropsWithChildren<CtaLinkInternalProps<TLink>> & Omit<React.ComponentProps<TLink>, keyof PropsWithChildren<CtaLinkInternalProps<TLink>>>;
export function CtaLink<TLink extends ElementWithAnchorProps = typeof Link>({
  children,
  Component = Link,
  href,
  analytics,
  onClickAnalyticsCallback,
  parameterActions,
  isReview,
  isMonetizing,
  finishType,
  type,
  skipShoppingFinishedEvent = false,
  shoppingFinishedId: overrideShoppingFinishedId,
  rel,
  ...rest
}: CtaLinkProps<TLink>): React.JSX.Element {
  const resolvedIsReview = isReview ?? href?.includes('/reviews/');
  const experimentalHref = useExperimentalApplyLink(href, type, resolvedIsReview);
  const chaseLandingPageHref = useChaseLandingPageLink(experimentalHref, type, resolvedIsReview);
  const paramsGenerator = useCallback(generateSearchParameters(parameterActions), [parameterActions]);
  const isHydrated = useIsMounted();
  const shoppingFinishedId = useConstant(() => uuid().replace(/-/g, ''));
  const {
    fireShoppingFinishedEvent,
    skuImpressionId
  } = useAnalyticsSkuViewedContext();
  const {
    pageViewId
  } = useAnalyticsPageView();
  const cardPosition = useCardPosition(analytics.productPosition);
  analytics = useMemo(() => cardPosition === analytics.productPosition ? analytics : {
    ...analytics,
    productPosition: cardPosition,
    marketplacePayload: analytics.marketplacePayload ? {
      ...analytics.marketplacePayload,
      element_position: cardPosition,
      section_position: cardPosition
    } : undefined
  }, [analytics, cardPosition]);
  if (!href) {
    throw new Error('Expected href to be defined but its not');
  }
  const myShoppingFinishedId = overrideShoppingFinishedId ?? shoppingFinishedId;
  const fireNonMonetizingPageAction = useNonMonetizingPageAction(isMonetizing);
  const fullHref_0 = useMemo(() => {
    let activeHref = chaseLandingPageHref;
    if (env.NEXT_PUBLIC_DEPLOYABLE_ENV !== 'prod' && env.NEXT_PUBLIC_DEPLOYABLE_ENV !== 'preview-wp-production') {
      activeHref = activeHref.replace('www.nerdwallet.com/redirect', 'stage.nerdwallet.biz/redirect');
    }
    if (!isHydrated) {
      return activeHref;
    }
    let fullHref = activeHref;
    const parsedUrl = new URL(fullHref);
    const effectiveAnalytics = fullHref !== href && analytics.marketplacePayload ? {
      ...analytics,
      marketplacePayload: {
        ...analytics.marketplacePayload,
        destination_url: fullHref
      }
    } : analytics;
    const params = paramsGenerator({
      ...effectiveAnalytics,
      type,
      navigationId: pageViewId,
      parentImpressionId: skuImpressionId,
      finishType: getFinishTypeForApplyLink(activeHref, finishType),
      impressionPageUrl: getCleanLocationHref()
    });
    const urlSearchParams = new URLSearchParams(parsedUrl.search);
    if (urlSearchParams.has('clickHeader_productId')) {
      urlSearchParams.delete('clickHeader_productId');
    }
    Object.entries(params).forEach(([key, value]: [string, SearchParamValue]) => {
      // THIS SHOULD REMAIN THIS WAY IN ORDER TO PREVENT ISSUES WITH THE TARGET ANALYTICS SERVICES
      // SEE: https://github.com/NerdWallet/cc-shopping-client/pull/509#discussion_r1282359781
      const stringValue: string = value === undefined ? 'undefined' : value.toString();
      urlSearchParams.append(key, stringValue);
    });
    parsedUrl.search = urlSearchParams.toString();
    fullHref = ctaLinkRegexParsing(parsedUrl);
    if (parameterActions.includes(CTASearchParams.SHOPPING_FINISH) && myShoppingFinishedId) {
      fullHref = transformApplyLink({
        applyLink: fullHref,
        shoppingFinishedId: myShoppingFinishedId
      });
    }
    // we shouldn't be providing query parameters to internal content
    if ((isReview || parsedUrl.pathname.includes('reviews')) && urlSearchParams.toString()) {
      datadogRum.addError(new Error('Review link has query parameters'), {
        message: 'A review link has query parameters',
        actions: JSON.stringify(parameterActions),
        targetHref: fullHref,
        finishType
      });
    }
    return fullHref;
  }, [analytics, isHydrated, parameterActions, isReview, myShoppingFinishedId, chaseLandingPageHref]);
  const handleOnClick = () => {
    !skipShoppingFinishedEvent && analytics.monetizable !== 'yes_assumed' && createHandleOnClick({
      fireShoppingFinishedEvent,
      fireNonMonetizingPageAction,
      destinationUrl: fullHref_0,
      overrideShoppingFinishedId: myShoppingFinishedId,
      monetizable: analytics.monetizable || 'no',
      type
    })();
    onClickAnalyticsCallback?.();
  };
  const safeRel = addNoopener(rel);
  return <Component {...rest} rel={safeRel} href={fullHref_0} onClick={handleOnClick}>
      {children}
    </Component>;
}